import { useState, useEffect } from 'react';
import { toast } from '@/hooks/ui/use-toast';
import { supabase as supabaseClient } from '@/integrations/supabase/client';
import { EmployeeStats } from '@/types/insights';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, differenceInHours, parseISO } from 'date-fns';

interface UseEmployeeStatsProps {
  businessId: string | null;
  userId?: string | null;
}

interface UseEmployeeStatsReturn {
  employeeStats: EmployeeStats | null;
  loading: boolean;
  refetch: () => void;
}

/**
 * Hook for fetching employee statistics for insights dashboard
 */
export const useEmployeeStats = (
  businessId: string | null,
  userId?: string | null
): UseEmployeeStatsReturn => {
  const [employeeStats, setEmployeeStats] = useState<EmployeeStats | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchEmployeeStats = async () => {
    if (!businessId) {
      setEmployeeStats(null);
      return;
    }

    setLoading(true);

    try {
      const now = new Date();
      const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday
      const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);

      // Fetch employees
      const { data: employees, error: employeesError } = await supabaseClient
        .from('employees')
        .select('id, name, created_at, contract_status')
        .eq('business_id', businessId);

      if (employeesError) throw employeesError;

      // Fetch shifts for this week
      const { data: weekShifts, error: shiftsError } = await supabaseClient
        .from('shifts')
        .select('id, employee_id, start_time, end_time')
        .eq('business_id', businessId)
        .gte('start_time', weekStart.toISOString())
        .lte('end_time', weekEnd.toISOString());

      if (shiftsError) throw shiftsError;

      // Fetch time logs for attendance analysis
      const { data: timeLogs, error: timeLogsError } = await supabaseClient
        .from('time_logs')
        .select('id, employee_id, event_type, scanned_at, created_at')
        .eq('business_id', businessId)
        .gte('scanned_at', weekStart.toISOString())
        .lte('scanned_at', weekEnd.toISOString());

      if (timeLogsError) throw timeLogsError;

      // Calculate statistics
      const totalEmployees = employees?.length || 0;
      const activeEmployees = employees?.filter(emp => emp.contract_status !== 'inactive').length || 0;

      // Calculate total hours this week
      const totalHoursThisWeek = (weekShifts || []).reduce((total, shift) => {
        if (shift.start_time && shift.end_time) {
          const hours = differenceInHours(parseISO(shift.end_time), parseISO(shift.start_time));
          return total + Math.max(0, hours);
        }
        return total;
      }, 0);

      // Calculate average hours per week per employee
      const averageHoursPerWeek = totalEmployees > 0 ? totalHoursThisWeek / totalEmployees : 0;

      // Calculate attendance stats
      const arrivalLogs = (timeLogs || []).filter(log => log.event_type === 'arrival');
      const lateArrivals = arrivalLogs.filter(log => {
        // Simple late arrival detection - can be enhanced with actual shift start times
        const logTime = new Date(log.scanned_at);
        const hour = logTime.getHours();
        return hour >= 9; // Consider late if arriving after 9 AM (can be customized)
      }).length;

      const attendanceRate = arrivalLogs.length > 0 ? 
        Math.round(((arrivalLogs.length - lateArrivals) / arrivalLogs.length) * 100) : 100;

      // Calculate overtime (shifts longer than 8 hours)
      const overtimeHours = (weekShifts || []).reduce((total, shift) => {
        if (shift.start_time && shift.end_time) {
          const hours = differenceInHours(parseISO(shift.end_time), parseISO(shift.start_time));
          return total + Math.max(0, hours - 8);
        }
        return total;
      }, 0);

      // Find top performer (employee with most shifts this week)
      const employeeShiftCounts: Record<string, number> = {};
      (weekShifts || []).forEach(shift => {
        if (shift.employee_id) {
          employeeShiftCounts[shift.employee_id] = (employeeShiftCounts[shift.employee_id] || 0) + 1;
        }
      });

      const topPerformerId = Object.entries(employeeShiftCounts)
        .sort(([,a], [,b]) => b - a)[0]?.[0];
      
      const topPerformer = topPerformerId ? 
        employees?.find(emp => emp.id === topPerformerId)?.name || 'Unknown' : 
        'No data';

      const stats: EmployeeStats = {
        totalEmployees,
        activeEmployees,
        averageHoursPerWeek: Math.round(averageHoursPerWeek * 10) / 10,
        topPerformer,
        attendanceRate,
        lateArrivals,
        totalHoursThisWeek: Math.round(totalHoursThisWeek * 10) / 10,
        overtimeHours: Math.round(overtimeHours * 10) / 10,
      };

      setEmployeeStats(stats);

    } catch (error: unknown) {
      console.error('Error fetching employee stats:', error);
      toast.error('Failed to fetch employee statistics', { 
        description: error instanceof Error ? error.message : String(error) 
      });
      setEmployeeStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployeeStats();
  }, [businessId, userId]);

  return {
    employeeStats,
    loading,
    refetch: fetchEmployeeStats,
  };
};